/**
 * M27 Geldtüte-Transport — the pure rules the three client parts share (Schulportal, Mitarbeiterportal courier, Büro Kasse-Kontrolle).
 * One barcode = one bag. The server decides every state; this file only reads and shows what it sends. No React, no network: tested in
 * shared/lib/__tests__/geldtuete.test.ts.
 */

/** Bag states in the order of precedence (the highest wins), plan §1: kontrolliert > zentrale > unterwegs > uebergeben > zu > voll > offen. */
export const TRANSPORT_ZUSTAENDE = ['offen', 'voll', 'zu', 'uebergeben', 'unterwegs', 'zentrale', 'kontrolliert'] as const
export type TransportZustand = (typeof TRANSPORT_ZUSTAENDE)[number]

export type TransportKanal = 'schulportal' | 'handy' | 'buero'

/** P81 / gt_v1: a bag that waits longer than this (calendar days, Hamburg date) is an alert in Kasse-Kontrolle and a quiet hint at the school. */
export const WARTE_TAGE_GRENZE = 14

/** The barcode of a Geldtüte: 6 to 14 digits (same rule as the Kassenbuch and the server). */
export const BARCODE_MIN = 6
export const BARCODE_MAX = 14

/** The formats the camera and the Büro scanner read. Code 128 / ITF / EAN are the bag labels; Code 39 and Codabar cover older labels. */
export const SCAN_FORMATE = ['code_128', 'itf', 'ean_13', 'ean_8', 'code_39', 'codabar'] as const

/** What was scanned or typed -> the digits of a bag, or null. A scanner may add CR/LF or spaces around the code; any other character makes it invalid. */
export function barcodeZiffern(roh: unknown): string | null {
  if (typeof roh !== 'string' && typeof roh !== 'number') return null
  const text = String(roh).trim()
  return new RegExp(`^\\d{${BARCODE_MIN},${BARCODE_MAX}}$`).test(text) ? text : null
}

/** Same rule as KassenbuchTag.barcode_key on the server: leading zeros stripped, '0' when all are zeros. */
export function barcodeSchluessel(ziffern: string): string {
  const ohne = ziffern.replace(/^0+/, '')
  return ohne === '' ? '0' : ohne
}

export function gleicheTuete(a: string | null | undefined, b: string | null | undefined): boolean {
  const x = barcodeZiffern(a)
  const y = barcodeZiffern(b)
  return !!x && !!y && barcodeSchluessel(x) === barcodeSchluessel(y)
}

// ── Time: server timestamps are UTC, the calendar day is the Hamburg day. Never the kitchen PC's timezone. ──

function alsDatum(zeit: string | Date | null | undefined): Date | null {
  if (!zeit) return null
  if (zeit instanceof Date) return Number.isNaN(zeit.getTime()) ? null : zeit
  // A date-time without a zone from the server is UTC. A plain date (yyyy-mm-dd) is a Hamburg calendar day at noon.
  const text = /^\d{4}-\d{2}-\d{2}$/.test(zeit) ? `${zeit}T12:00:00Z` : /(?:Z|[+-]\d{2}:?\d{2})$/i.test(zeit) ? zeit : `${zeit}Z`
  const d = new Date(text)
  return Number.isNaN(d.getTime()) ? null : d
}

/** ISO calendar day (yyyy-mm-dd) of a moment in Hamburg. null for anything that is not a time. */
export function berlinTag(zeit: string | Date | null | undefined): string | null {
  const d = alsDatum(zeit)
  if (!d) return null
  const teile = new Intl.DateTimeFormat('en-CA', { timeZone: 'Europe/Berlin', year: 'numeric', month: '2-digit', day: '2-digit' }).formatToParts(d)
  const p = (typ: string) => teile.find(x => x.type === typ)?.value
  return `${p('year')}-${p('month')}-${p('day')}`
}

/** The server sent a plain calendar day (yyyy-mm-dd), not a moment: the state comes from the days of the bag, so there is no time of day to show. */
export const istNurTag = (zeit: unknown): boolean => typeof zeit === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(zeit)

/** "08:50" in Hamburg time, or '–' (also for a plain day: it has no time of day). */
export function berlinUhr(zeit: string | Date | null | undefined): string {
  const d = alsDatum(zeit)
  if (!d || istNurTag(zeit)) return '–'
  return new Intl.DateTimeFormat('de-DE', { timeZone: 'Europe/Berlin', hour: '2-digit', minute: '2-digit', hourCycle: 'h23' }).format(d)
}

/** Whole calendar days from `von` to `bis` (both ISO days). Negative when `bis` is earlier. NaN when one is not a day. */
export function kalenderTage(von: string, bis: string): number {
  const a = /^(\d{4})-(\d{2})-(\d{2})/.exec(von)
  const b = /^(\d{4})-(\d{2})-(\d{2})/.exec(bis)
  if (!a || !b) return NaN
  const tag = (m: RegExpExecArray) => Date.UTC(+m[1], +m[2] - 1, +m[3]) / 86_400_000
  return Math.round(tag(b) - tag(a))
}

/** Calendar days between a server time and `heute` (an ISO Hamburg day), never below 0. null when the time is unknown. */
export function tageSeit(zeit: string | Date | null | undefined, heute: string): number | null {
  const tag = berlinTag(zeit)
  if (!tag) return null
  const n = kalenderTage(tag, heute)
  return Number.isNaN(n) ? null : Math.max(0, n)
}

export interface Zeitpunkt {
  /** The moment is on `heute`. */
  heute: boolean
  /** Short date in the given locale, day first, e.g. "Mo 12.10." (de-DE) or "Mon 12/10" (en-GB). */
  datum: string
  /** "08:50"; '–' when `nurTag`. */
  uhr: string
  /** The input was a plain day (yyyy-mm-dd) without a time of day: show the date only, never a made-up hour. */
  nurTag: boolean
}

/** A server time split for display: callers add their own words ("Heute", "Uhr"). */
export function zeitpunkt(zeit: string | Date | null | undefined, heute: string, locale = 'de-DE'): Zeitpunkt | null {
  const d = alsDatum(zeit)
  const tag = berlinTag(d)
  if (!d || !tag) return null
  // "Di 27.10." (de), "Tue 27/10" (en-GB): the weekday word without its abbreviation dot, then the day and month the way the language writes them.
  const wochentag = new Intl.DateTimeFormat(locale, { timeZone: 'Europe/Berlin', weekday: 'short' }).format(d).replace(/\.$/, '')
  const tagMonat = new Intl.DateTimeFormat(locale, { timeZone: 'Europe/Berlin', day: '2-digit', month: '2-digit' }).format(d)
  return { heute: tag === heute, datum: `${wochentag} ${tagMonat}`, uhr: berlinUhr(zeit), nurTag: istNurTag(zeit) }
}

/** A list from an answer that is either the array itself or an object that holds it (under one of the keys, else its only array). */
export function alsListe<T>(antwort: unknown, ...schluessel: string[]): T[] {
  if (Array.isArray(antwort)) return antwort as T[]
  if (antwort && typeof antwort === 'object') {
    const o = antwort as Record<string, unknown>
    for (const k of schluessel) if (Array.isArray(o[k])) return o[k] as T[]
    const arrays = Object.values(o).filter(Array.isArray)
    if (arrays.length === 1) return arrays[0] as T[]
  }
  return []
}

// ── Pickup scan (Schulportal "Abholung hier am Laptop" and the courier phone): the same rules. The person only scans; `erwartet` is never listed, only counted. ──

export interface AbholZustand {
  /** The bags the school handed over (barcodes as the server sent them). */
  erwartet: readonly string[]
  /** Expected bags that were scanned (as listed in `erwartet`), in scan order. */
  gescannt: readonly string[]
  /** Bags the person took although the school did not hand them over (digits as scanned). Booked with the deviation `ohneUebergabe`. */
  extra: readonly string[]
  /** A scanned bag that is not expected: waits for "Trotzdem aufnehmen" or "Verwerfen". */
  warnung: string | null
}

export type AbholErgebnis = 'gescannt' | 'schonGescannt' | 'schonAufgenommen' | 'nichtUebergeben' | 'ungueltig'

export const neuerAbholZustand = (erwartet: readonly string[]): AbholZustand => ({ erwartet, gescannt: [], extra: [], warnung: null })

/** One scan or typed code. An unknown bag is never taken silently: it raises the warning, the person decides. */
export function abholScan(z: AbholZustand, roh: unknown): { z: AbholZustand; ergebnis: AbholErgebnis; barcode?: string } {
  const frei: AbholZustand = { ...z, warnung: null }
  const ziffern = barcodeZiffern(roh)
  if (!ziffern) return { z: frei, ergebnis: 'ungueltig' }
  const treffer = z.erwartet.find(b => gleicheTuete(b, ziffern))
  if (!treffer) {
    if (z.extra.some(b => gleicheTuete(b, ziffern))) return { z: frei, ergebnis: 'schonAufgenommen', barcode: ziffern }
    return { z: { ...frei, warnung: ziffern }, ergebnis: 'nichtUebergeben', barcode: ziffern }
  }
  if (z.gescannt.includes(treffer)) return { z: frei, ergebnis: 'schonGescannt', barcode: treffer }
  return { z: { ...frei, gescannt: [...z.gescannt, treffer] }, ergebnis: 'gescannt', barcode: treffer }
}

/** "Trotzdem aufnehmen": the warned bag joins as an extra. */
export function abholAufnehmen(z: AbholZustand): AbholZustand {
  const b = z.warnung
  if (!b) return z
  return { ...z, warnung: null, extra: z.extra.some(x => gleicheTuete(x, b)) ? z.extra : [...z.extra, b] }
}

export const abholVerwerfen = (z: AbholZustand): AbholZustand => (z.warnung ? { ...z, warnung: null } : z)

/** The × of the list: the person takes one scanned (or added) bag out again. Nothing else changes; a warning that waits stays. */
export function abholEntfernen(z: AbholZustand, barcode: string): AbholZustand {
  return { ...z, gescannt: z.gescannt.filter(b => b !== barcode), extra: z.extra.filter(b => b !== barcode) }
}

/** How many bags the person books: scanned expected bags plus extras. */
export const abholAnzahl = (z: AbholZustand): number => z.gescannt.length + z.extra.length

/** How many handed-over bags are still not scanned: the only number the pickup screens show about what is expected (never a code, never an amount). */
export const abholWartend = (z: AbholZustand): number => Math.max(0, z.erwartet.length - z.gescannt.length)

/**
 * The list refreshed under a running scan (a bag left the school, or one was added): keeps what was scanned as long as the bag is still expected,
 * moves a scanned bag that is no longer expected out. Nothing is lost silently: a bag that is no longer expected is simply not booked as handed over.
 */
export function abholNeuErwartet(z: AbholZustand, erwartet: readonly string[]): AbholZustand {
  return { ...z, erwartet, gescannt: z.gescannt.filter(b => erwartet.includes(b)) }
}

/** Debounce for a camera that sees the same code in many frames: true = this code counts now, false = the same code within `fensterMs`. */
export function neuerEntpreller(fensterMs: number, jetzt: () => number = () => Date.now()): (code: string) => boolean {
  let letzter = ''
  let wann = -Infinity
  return (code: string) => {
    const t = jetzt()
    if (code === letzter && t - wann < fensterMs) return false
    letzter = code
    wann = t
    return true
  }
}
